import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { enqueueSnackbar } from "notistack";

import { Box } from "@mui/material";
import { BrokenImage } from "@mui/icons-material";

import { TrainDefectSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { DataExportButton } from "../../../../../../components/common/buttons/export-button";
import { DefectsTable } from "./table";
import { downloadCsv } from "../../../../../../utils/download-csv";
import { IDefectsValues, IDefectResponse } from "./interfaces";
import { useTrainTables } from "../hooks/use-train-tables";
import { useToFakeUtcISO } from "../../../../../../hooks";
import { formatDateHumanReadable } from "../../../../../../utils";

export const Defects: FC<{ isActiveTableTab?: boolean }> = props => {
  const { isActiveTableTab } = props;

  const [defects, setDefects] = useState<IDefectResponse[]>([]);

  const {
    trainId,
    startTimestamp,
    endTimestamp,
    isRecorderMode,
    recorderWindowStart,
    sortColumn,
    sortOrder,
    handleSort,
    canFetch,
  } = useTrainTables({
    isActiveTableTab,
    defaultSortColumn: TrainDefectSortColumn.Date,
    onReset: () => setDefects([]),
  });

  const { formatMessage } = useIntl();
  const toFakeUtcISO = useToFakeUtcISO();

  const headersId = "pages.scheme.defects.headers";
  const noDefects = formatMessage({ id: "pages.scheme.defects.empty" });

  const { fn: getDefects, isLoading } = useApiCall(
    (api, { trainId, startTimestamp, endTimestamp, sortColumn, sortOrder }: IDefectsValues) => {
      return api
        .fetchJson({
          url: `/trains/${trainId}/defects`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
            sortColumn,
            sortOrder,
          },
        })
        .then((json: IDefectResponse[] | null) => {
          return json ?? [];
        });
    },
    { success: false },
  );

  const applyFakeUtcToDefects = (items: IDefectResponse[]): IDefectResponse[] =>
    items.map(item => ({
      ...item,
      Date: toFakeUtcISO(item.Date),
    }));

  const handleExport = (): void => {
    try {
      if (!defects || !defects.length) {
        return;
      }

      if (trainId) {
        const date = toFakeUtcISO(new Date()).split("T")[0];
        downloadCsv(
          defects.map(defect => {
            const { Position, CreationType, DefectName, DefectStatus, Rate, Date } = defect;

            return {
              rate: Rate,
              position: Position,
              date: formatDateHumanReadable(Date),
              name: DefectName,
              status: formatMessage({ id: `enums.defectStatus.${DefectStatus}` }),
              source: formatMessage({ id: `enums.defectSource.${CreationType}` }),
            };
          }),
          `train-defects-report-${trainId}-${date}.csv`,
          [
            { field: "rate", title: formatMessage({ id: `${headersId}.growthRate` }) },
            { field: "position", title: formatMessage({ id: `${headersId}.position` }) },
            { field: "date", title: formatMessage({ id: `${headersId}.date` }) },
            { field: "name", title: formatMessage({ id: `${headersId}.name` }) },
            { field: "status", title: formatMessage({ id: `${headersId}.status` }) },
            { field: "source", title: formatMessage({ id: `${headersId}.source` }) },
          ],
        );
      }
    } catch (error: unknown) {
      console.error("Error during export:", error);
      enqueueSnackbar(formatMessage({ id: "snackbar.exportError" }), { variant: "error" });
    }
  };

  useEffect(() => {
    if (!canFetch) return;

    void getDefects(undefined, {
      trainId,
      startTimestamp,
      endTimestamp,
      ...(isRecorderMode ? {} : { sortColumn, sortOrder }),
    }).then(json => {
      if (!Array.isArray(json)) return;

      if (isRecorderMode) {
        setDefects(prev => {
          const converted = applyFakeUtcToDefects(json);
          const newItems = converted.filter(
            item => !prev.some(prevItem => prevItem.idDiagnoseDefect === item.idDiagnoseDefect),
          );
          const updated = [...prev, ...newItems];
          const filtered = updated.filter(item => new Date(item.Date) >= new Date(recorderWindowStart));
          return filtered;
        });
      } else {
        setDefects(applyFakeUtcToDefects(json));
      }
    });
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    sortColumn,
    sortOrder,
    isRecorderMode,
    recorderWindowStart,
    isActiveTableTab,
  ]);

  return (
    <ProgressOverlay
      isLoading={isLoading}
      wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {!defects.length ? (
          <EmptyPlaceholder icon={<BrokenImage />} text={noDefects} />
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", height: "34px" }}>
              <DataExportButton handleExport={handleExport} />
            </Box>
            <DefectsTable defects={defects} handleSort={handleSort} sortColumn={sortColumn} sortOrder={sortOrder} />
          </>
        )}
      </Box>
    </ProgressOverlay>
  );
};
